import { DataObject, Delete, Place } from "@mui/icons-material"
import { Box, Button, Paper, Stack, Tooltip, Typography } from "@mui/material"
import moment from "moment"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { formatAge, LockerVitals, SyncFreshness } from "./saLockerVitals"

/** Sync age is the one fact on this panel that can be actively wrong. */
const FRESHNESS_COLOR: Record<SyncFreshness, string> = {
  fresh: "success.main",
  aging: "warning.main",
  stale: "error.main",
  never: "text.disabled",
}

const Row: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ minWidth: 0, textAlign: "right" }}>{children}</Box>
  </Stack>
)

const Val: FC<{ children: React.ReactNode; color?: string }> = ({ children, color }) => (
  <Typography
    variant="caption"
    fontFamily="monospace"
    sx={{ color: color || "text.primary", overflowWrap: "anywhere" }}
  >
    {children}
  </Typography>
)

/**
 * The facts an operator checks alongside the locker itself: how fresh the
 * partner snapshot is, how to reach the raw payload, where the unit physically
 * is, and the destructive action — last, so it is never on the path between
 * reading a value and editing a field.
 */
export const SaLockerDetailsPanel: FC<{
  locker: Locker
  vitals: LockerVitals
  onOpenSnapshot: () => void
  onDelete: () => void
}> = ({ locker, vitals, onOpenSnapshot, onDelete }) => {
  const { t } = useTranslation()

  const address = [locker.address1, locker.city, locker.province, locker.postalCode]
    .filter(Boolean)
    .join(", ")
  const freshness = FRESHNESS_COLOR[vitals.syncFreshness]

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      alignItems="stretch"
      sx={{ "& > *": { flex: 1, minWidth: 0 } }}
    >
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t("saLockerConsole.partnerLink")}
        </Typography>
        <Stack direction="column" spacing={0.75}>
          <Row label={t("saLockerConsole.lastSync")}>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
              <Box
                sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: freshness, flexShrink: 0 }}
              />
              <Val color={freshness}>
                {vitals.syncAgeMs === undefined
                  ? t("saLockerConsole.never")
                  : `${formatAge(vitals.syncAgeMs)} ${t("saLockerConsole.ago")}`}
              </Val>
            </Stack>
          </Row>
          {vitals.lastSyncMs !== undefined && (
            <Row label="">
              <Typography variant="caption" fontFamily="monospace" color="text.disabled">
                {moment(vitals.lastSyncMs).format("YYYY-MM-DD HH:mm:ss")}
              </Typography>
            </Row>
          )}
          <Row label={t("saLockerConsole.partnerId")}>
            <Val>{vitals.partnerId || "—"}</Val>
          </Row>
          {vitals.enabled !== null && (
            <Row label={t("saLockerConsole.partnerEnabled")}>
              <Val color={vitals.enabled ? "success.main" : "error.main"}>
                {vitals.enabled ? t("yes") : t("no")}
              </Val>
            </Row>
          )}
          {vitals.timezone && (
            <Row label={t("saLockerConsole.timezone")}>
              <Val>{vitals.timezone}</Val>
            </Row>
          )}
        </Stack>
        <Button
          size="small"
          startIcon={<DataObject fontSize="small" />}
          onClick={onOpenSnapshot}
          disabled={!vitals.hasSnapshot}
          sx={{ mt: 1, textTransform: "none" }}
        >
          {t("saLockerConsole.rawSnapshot")}
        </Button>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t("saLockerConsole.location")}
        </Typography>
        {address ? (
          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
            {address}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        )}
        {locker.latitude && locker.longitude && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
            <Place sx={{ fontSize: 14, color: "text.disabled" }} />
            <Tooltip title={t("saLockerConsole.openInMaps")}>
              <Typography
                component="a"
                href={`https://www.google.com/maps?q=${locker.latitude},${locker.longitude}`}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                fontFamily="monospace"
                color="primary"
                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {locker.latitude}, {locker.longitude}
              </Typography>
            </Tooltip>
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "error.light" }}>
        <Typography variant="caption" color="error.main" display="block" sx={{ mb: 1 }}>
          {t("saLockerConsole.dangerZone")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {t("lockerDeleteWarning")}
        </Typography>
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<Delete fontSize="small" />}
          onClick={onDelete}
          sx={{ textTransform: "none" }}
        >
          {t("lockerDelete")}
        </Button>
      </Paper>
    </Stack>
  )
}

export default SaLockerDetailsPanel
